import { App, Form, Modal, Select, Space } from "antd";
import { CloseSquareFilled, SaveFilled } from '@ant-design/icons';
import { useEffect, useMemo, useState } from "react";
import execute from "hooks/execute";
import { protectedResources } from "config/msalConfig";

function parseModuleShortNames(modulesValue) {
    if (!modulesValue || typeof modulesValue !== 'string') {
        return [];
    }

    return modulesValue
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
}

export const DataEditor = ({ customer, isOpen, dataItem, onDataEditorFinished, onDataEditorCancelled }) => {
    const [form] = Form.useForm();
    const { message } = App.useApp();

    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);

    const moduleOptions = useMemo(() => {
        const modules = parseModuleShortNames(customer?.Modules || '');
        return modules.map((module) => ({ value: module, label: module }));
    }, [customer]);

    const fetchRolesByModule = async (moduleShortName) => {
        if (!moduleShortName || !customer?.UserID) {
            setRoles([]);
            return;
        }

        try {
            setLoading(true);
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/customeUserRoles/${customer.UserID}/modules/${moduleShortName}/roles`
            );

            if (response?.success && Array.isArray(response.data)) {
                const roleOptions = response.data.map((item) => ({
                    value: item.RoleId,
                    label: item.RoleName
                }));
                setRoles(roleOptions);
            } else {
                setRoles([]);
            }
        } catch (error) {
            setRoles([]);
            message.error('Failed to fetch roles: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDataEditFinished = async (values) => {
        const payload = {
            UserId: customer.UserID,
            ModuleShortName: values.ModuleShortName,
            RoleIds: values.RoleIds
        };

        try {
            const response = await execute('POST', `${protectedResources.api.baseEndPoint}/customeUserRolesValues`, payload);
            if (response?.success) {
                message.success('Customer user role assignments added successfully');
                onDataEditorFinished(response.data);
            } else {
                message.error(response?.message || 'Failed to assign roles');
            }
        } catch (error) {
            message.error(error?.response?.data?.message || error.message || 'Failed to assign roles');
        }
    };

    const handleDataEditCancelled = () => {
        onDataEditorCancelled();
    };

    useEffect(() => {
        if (isOpen) {
            form.setFieldsValue({
                ModuleShortName: undefined,
                RoleIds: []
            });
            setRoles([]);
        }
    }, [isOpen, form]);

    return (
        <Modal
            open={isOpen}
            title="Add CustomerUser Role Assignment"
            okText={<Space><SaveFilled />Save</Space>}
            cancelText={<Space><CloseSquareFilled />Cancel</Space>}
            okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
            onCancel={handleDataEditCancelled}
            destroyOnClose
            modalRender={(dom) => (
                <Form
                    layout="horizontal"
                    form={form}
                    name="dataEditorForm"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    clearOnDestroy
                    onFinish={handleDataEditFinished}
                >
                    {dom}
                </Form>
            )}
        >
            <Form.Item
                name="ModuleShortName"
                label="Modules"
                rules={[{ required: true, message: 'Module is required' }]}
            >
                <Select
                    style={{ width: '100%' }}
                    loading={loading}
                    options={moduleOptions}
                    placeholder="Select module"
                    onChange={(value) => {
                        form.setFieldValue('RoleIds', []);
                        fetchRolesByModule(value);
                    }}
                />
            </Form.Item>

            <Form.Item
                name="RoleIds"
                label="Select Role"
                rules={[{ required: true, message: 'At least one role is required' }]}
            >
                <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    loading={loading}
                    options={roles}
                    placeholder="Select role(s)"
                />
            </Form.Item>
        </Modal>
    );
};
